import { Injectable, inject, signal } from '@angular/core';

import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';

import {
  CanvasDataset,
  CANVAS_PUBLISHED_SEARCH_CRITERION,
  extractCatalogueItemsFromResponse,
  mapCatalogueItemToCanvasDataset,
} from '../canvas-catalogue.util';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT = 'itemCreatedAt:desc';
const SEARCH_DEBOUNCE_MS = 300;

export interface ChartsDatasetsFilters {
  organisations: string[];
  tags: string[];
  organizationTypes: string[];
  accessPolicies: string[];
  themes: string[];
}

const EMPTY_FILTERS: ChartsDatasetsFilters = {
  organisations: [],
  tags: [],
  organizationTypes: [],
  accessPolicies: [],
  themes: [],
};

@Injectable()
export class ChartsDatasetsService {
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly datasets = signal<CanvasDataset[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly searchQuery = signal('');

  private resourceServerUrls: string[] = [];
  private filters: ChartsDatasetsFilters = { ...EMPTY_FILTERS };
  private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private loadRequestId = 0;

  async loadDatasets(resourceServerUrls: string[]): Promise<void> {
    const urls = [...new Set(resourceServerUrls.map((u) => u.trim()).filter(Boolean))];
    if (urls.length === 0) return;

    this.resourceServerUrls = urls;
    this.filters = { ...EMPTY_FILTERS };
    this.searchQuery.set('');
    await this.fetchDatasets('');
  }

  searchDatasets(query: string): void {
    this.searchQuery.set(query);

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    const trimmed = query.trim();
    if (trimmed.length > 0 && trimmed.length < 3) return;

    this.searchDebounceTimer = setTimeout(() => {
      void this.fetchDatasets(trimmed);
    }, SEARCH_DEBOUNCE_MS);
  }

  applyFilters(filters: ChartsDatasetsFilters): void {
    this.filters = { ...filters };
    void this.fetchDatasets(this.searchQuery().trim());
  }

  resetSearchAndFilters(): void {
    this.filters = { ...EMPTY_FILTERS };
    this.searchQuery.set('');
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = undefined;
    }
  }

  private buildSearchCriteria(): SearchCriteria[] {
    const criteria: SearchCriteria[] = [
      CANVAS_PUBLISHED_SEARCH_CRITERION,
      {
        searchType: 'flattenedTerm',
        field: 'resourceServer.url',
        values: this.resourceServerUrls,
      },
    ];

    if (this.filters.organisations.length > 0) {
      criteria.push({ searchType: 'term', field: 'organization', values: this.filters.organisations });
    }
    if (this.filters.tags.length > 0) {
      criteria.push({ searchType: 'term', field: 'tags', values: this.filters.tags });
    }
    if (this.filters.organizationTypes.length > 0) {
      criteria.push({ searchType: 'term', field: 'organizationType', values: this.filters.organizationTypes });
    }
    if (this.filters.accessPolicies.length > 0) {
      criteria.push({ searchType: 'term', field: 'accessPolicy', values: this.filters.accessPolicies });
    }
    if (this.filters.themes.length > 0) {
      criteria.push({ searchType: 'term', field: 'concepts', values: this.filters.themes });
    }

    return criteria;
  }

  private async fetchDatasets(query: string): Promise<void> {
    if (this.resourceServerUrls.length === 0) return;

    const requestId = ++this.loadRequestId;
    this.loading.set(true);
    this.error.set(null);

    try {
      const response = await this.catalogueApi.searchAllAssets(
        query,
        ['Dataset'],
        [],
        1,
        DEFAULT_PAGE_SIZE,
        DEFAULT_SORT,
        this.buildSearchCriteria(),
        false,
        { autoComplete: true, fuzzy: true },
      );

      if (requestId !== this.loadRequestId) return;

      const datasets = extractCatalogueItemsFromResponse(response)
        .filter((item) => catalogueItemHasPlotSchema(item))
        .map(mapCatalogueItemToCanvasDataset)
        .filter((item): item is CanvasDataset => item != null);

      this.datasets.set(datasets);
    } catch {
      if (requestId !== this.loadRequestId) return;

      this.error.set('Failed to load datasets.');
      this.datasets.set([]);
    } finally {
      if (requestId === this.loadRequestId) {
        this.loading.set(false);
      }
    }
  }
}

/** Charts modal: only datasets that declare a non-empty plotSchema can be charted. */
function catalogueItemHasPlotSchema(item: unknown): boolean {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const plotSchema = (item as Record<string, unknown>)['plotSchema'];
  return Array.isArray(plotSchema) && plotSchema.length > 0;
}
